import { useState } from 'react'
import { User, ToggleLeft, ArrowDownAZ, Wallet, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'

// Salários Atuais dos Funcionários (WDESFUN / RDESFUN do ERP) — último salário lançado de cada um

interface Salario { motcod: string; nome: string; alterado: string; valorHora: number; salario: number; outros: number; encargos: number; total: number }

const dataBr = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const moeda = (n: number) => n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

export default function RelatorioSalariosAtuais() {
  const { empresaCod } = useEmpresa()
  const [funcCod, setFuncCod] = useState('')
  const [funcNome, setFuncNome] = useState('')
  const [situacao, setSituacao] = useState('')
  const [ordem, setOrdem] = useState<'C' | 'N' | 'T'>('C')
  const [res, setRes] = useState<{ linhas: Salario[]; totalGeral: number } | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  const params = () => ({ motcod: funcCod, situacao, ordem, empresa: empresaCod ?? 1 })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/salarios', { params: params() })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get('/funcionarios/relatorio/salarios', { params: { ...params(), formato }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'salarios-atuais.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch {
      setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.')
    } finally {
      setGerando('')
    }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'right', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'right', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Wallet size={18} color="#fff" />
            </span>
            <div>
              <h2>Salários Atuais dos Funcionários</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Último salário lançado de cada funcionário, com outros e encargos</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Funcionário" icon={User}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ minWidth: 300 }}>
                <LookupSelect endpoint="/funcionarios" extraParams={{ status: 'todos' }} codKey="motcod" descKey="motnome" tagKey="motcod"
                  codValue={funcCod} descValue={funcNome}
                  onSelect={(cod, desc) => { setFuncCod(cod); setFuncNome(desc) }}
                  placeholder="Todos" listWidth={320} />
              </div>
              {funcCod
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => { setFuncCod(''); setFuncNome('') }}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>

          <Linha label="Situação" icon={ToggleLeft}>
            <select value={situacao} onChange={e => setSituacao(e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">Todos</option>
              <option value="A">Ativos</option>
              <option value="I">Inativos</option>
            </select>
          </Linha>

          <Linha label="Classificação" icon={ArrowDownAZ}>
            <select value={ordem} onChange={e => setOrdem(e.target.value as 'C' | 'N' | 'T')} style={{ maxWidth: 200 }}>
              <option value="C">Código</option>
              <option value="N">Nome</option>
              <option value="T">Maior total</option>
            </select>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {res.linhas.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum funcionário com salário lançado.</div>}

            {res.linhas.length > 0 && (
              <>
                <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ ...th, textAlign: 'left' }}>Código</th><th style={{ ...th, textAlign: 'left' }}>Funcionário</th>
                        <th style={{ ...th, textAlign: 'left' }}>Alterado</th><th style={th}>Valor Hora</th><th style={th}>Salário</th>
                        <th style={th}>Outros</th><th style={th}>% Encargos</th><th style={th}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.linhas.map((l, i) => (
                        <tr key={l.motcod} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, textAlign: 'left', fontWeight: 600 }}>{l.motcod}</td>
                          <td style={{ ...td, textAlign: 'left' }}>{l.nome}</td>
                          <td style={{ ...td, textAlign: 'left' }}>{dataBr(l.alterado)}</td>
                          <td style={td}>{moeda(l.valorHora)}</td>
                          <td style={td}>{moeda(l.salario)}</td>
                          <td style={td}>{moeda(l.outros)}</td>
                          <td style={td}>{moeda(l.encargos)}</td>
                          <td style={{ ...td, fontWeight: 700, color: '#1b3f7b' }}>{moeda(l.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <span>TOTAL GERAL · {res.linhas.length} funcionário(s)</span>
                  <span>R$ {moeda(res.totalGeral)}</span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
